import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// Pay application detail (G702/G703): autosaving billing grid where typing
// qty, $, or %-to-date fills in the others; live cover-sheet math; overbilling
// guardrails; stored materials; payments; attachments (presign→PUT→register
// chips); AI Import Progress (ConsLog draft or an uploaded pay estimate,
// KC-87) with a review step; the send-for-signatures chain builder.
export default class extends Controller {
  static targets = [
    "row", "saveStatus", "statusPill", "footCheck",
    "g702Original", "g702NetCo", "g702Current", "g702Completed", "g702Retainage",
    "g702Earned", "g702Previous", "g702Due", "g702Balance", "g702UnitPrices",
    "footScheduled", "footPrevious", "footPeriod", "footStored", "footCompleted", "footBalance",
    "overbilledBanner", "overbilledText", "retainageInput", "suggestRetainageBtn",
    "periodStartInput", "periodEndInput",
    "submitModal", "submitModalDue", "rejectModal", "rejectReason", "voidModal",
    "reopenModal", "reopenReason",
    "paymentModal", "paymentAmount", "paymentDate", "paymentMethod", "paymentReference",
    "paymentsBody", "paymentsTable", "paymentsEmpty",
    "storedModal", "storedBody", "storedEmpty",
    "fileInput", "attachmentList", "attachmentEmpty",
    "aiDraftBtn", "chainModal", "chainRows", "chainRowTemplate", "chainPanel", "submitBtn",
    "aiReviewModal", "aiReviewRows", "aiReviewAll", "aiReviewApply", "aiReviewTotals",
    "aiImportModal", "aiImportChoices", "aiImportDrop", "aiImportInput", "aiImportProgress",
    "footTotalPct", "chainSumCurrent", "chainSumCompleted", "chainSumRetainage",
    "chainSumPrevious", "chainSumDue", "prevBand", "prevChevron"
  ]
  static values = {
    editable: Boolean, canManage: Boolean, retainage: Number,
    updateUrl: String, updateLinesUrl: String, refreshUrl: String, submitUrl: String,
    internalApproveUrl: String, approveUrl: String, rejectUrl: String, reviseUrl: String,
    reopenUrl: String,
    voidUrl: String, deleteUrl: String, deleteBlocked: Boolean, deleteBlockedMessage: String,
    suggestRetainageUrl: String, paymentUrl: String, storedUrl: String,
    presignUrl: String, registerUrl: String, attachmentsUrl: String,
    aiDraftUrl: String, aiStatusUrl: String, aiImportUrl: String, followupUrl: String, chainUrl: String
  }

  connect() {
    this._dirty = {}
    this._aiBtnHtml = this.hasAiDraftBtnTarget ? this.aiDraftBtnTarget.innerHTML : ""
    this._loadAttachments()
    this._showPendingFlash()
    this._initPrevCols()
  }

  // ── Previous column group collapse (Larry 2026-07-23) ────────────────────
  // Band header click collapses Previous down to just its $ column (QTY and %
  // hide — Phelipe 2026-07-23). Default open; the choice sticks per browser.
  togglePrevCols() {
    this._applyPrevCols(!this._prevCollapsed)
    try { localStorage.setItem("builder-pr.prev-cols", this._prevCollapsed ? "collapsed" : "open") } catch {}
  }

  _initPrevCols() {
    this._prevCollapsed = false
    if (!this.hasPrevBandTarget) return
    let saved = null
    try { saved = localStorage.getItem("builder-pr.prev-cols") } catch {}
    if (saved === "collapsed") this._applyPrevCols(true)
  }

  _applyPrevCols(collapsed) {
    this._prevCollapsed = collapsed
    this.prevBandTarget.colSpan = collapsed ? 1 : 3
    if (this.hasPrevChevronTarget) this.prevChevronTarget.style.transform = collapsed ? "rotate(-90deg)" : ""
    this.element.querySelectorAll("[data-prev-col]").forEach((el) => { el.hidden = collapsed })
  }

  _showPendingFlash() {
    const msg = sessionStorage.getItem("builder-finance.flash")
    if (!msg) return
    sessionStorage.removeItem("builder-finance.flash")
    this._showToast(msg)
  }

  disconnect() { clearTimeout(this._saveTimer); clearInterval(this._aiPoll) }

  // ── Grid math ──────────────────────────────────────────────────────────
  lineInput(e) {
    const row = e.target.closest("tr")
    const field = e.target.dataset.field
    const sched = parseFloat(row.dataset.scheduled) || 0
    const prev = parseFloat(row.dataset.previous) || 0
    const unitPrice = parseFloat(row.dataset.unitPrice) || 0
    // Unit price lines (K8-13): quantity used × unit price, at least the
    // minimum the first time; no scheduled value, % or balance to show.
    const unitPriceLine = row.dataset.unitPriceLine === "1"
    const minimum = parseFloat(row.dataset.minimum) || 0
    const estQty = parseFloat(row.dataset.estimatedQty) || 0
    const maxAmount = parseFloat(row.dataset.maxAmount) || 0
    const prevQty = parseFloat(row.dataset.previousQty) || 0
    const get = (f) => {
      const el = row.querySelector(`[data-field="${f}"]`)
      return el ? this._num(el.value) : 0
    }
    const set = (f, v) => {
      const el = row.querySelector(`[data-field="${f}"]`)
      if (el && el !== e.target) el.value = v
    }

    let amount = get("this_period_amount")
    const stored = get("stored_materials")

    // !== 0 (not > 0): credit lines ("bid cut") carry a negative unit price /
    // scheduled value and must calculate too — the sign math works unchanged.
    // The % field is THIS application's share of scheduled value (cumulative
    // lives in the Total % column — Larry 2026-07-21).
    if (field === "this_period_qty" && unitPrice !== 0) {
      amount = this._round(get("this_period_qty") * unitPrice)
      set("this_period_amount", this._fmtMoneyEntry(amount))
    } else if (field === "pct" && sched !== 0) {
      amount = this._round((sched * get("pct")) / 100)
      set("this_period_amount", this._fmtMoneyEntry(amount))
      if (unitPrice !== 0) set("this_period_qty", this._fmtQty(this._round3(amount / unitPrice)))
    } else if (field === "this_period_amount") {
      if (unitPrice !== 0) set("this_period_qty", this._fmtQty(this._round3(amount / unitPrice)))
    }
    if (unitPriceLine) {
      const qty = get("this_period_qty")
      amount = this._round(qty * unitPrice)
      if (prev === 0 && qty > 0 && minimum > amount) amount = minimum
      set("this_period_amount", this._fmtMoneyEntry(amount))
    }

    const total = this._round(prev + amount + stored)
    if (field !== "pct") set("pct", sched !== 0 ? this._round((amount / sched) * 100) : 0)

    row.querySelector('[data-cell="total"]').textContent = "$" + this._fmt(total)
    row.querySelector('[data-cell="balance"]').textContent = unitPriceLine ? "—" : "$" + this._fmt(sched - total)
    const totalQtyCell = row.querySelector('[data-cell="total_qty"]')
    if (totalQtyCell) totalQtyCell.textContent = this._fmtQty(prevQty + get("this_period_qty"))
    const totalPctCell = row.querySelector('[data-cell="total_pct"]')
    if (totalPctCell) totalPctCell.textContent = sched !== 0 ? this._pctFmt((total / sched) * 100) : "—"
    // Over-billed (red) wins; otherwise any line with billing this period
    // gets a light-green "you billed this line" tint. Negative (credit) lines
    // invert: over-billed means going MORE negative than scheduled.
    // A unit price line over-bills only by passing its optional limits
    // (not-to-exceed amount, estimated quantity) — a warning, never a block.
    const overbilled = unitPriceLine
      ? ((maxAmount > 0 && total > maxAmount + 0.02) || (estQty > 0 && prevQty + get("this_period_qty") > estQty + 0.001))
      : (sched < 0 ? total < sched - 0.02 : total > sched + 0.02)
    row.classList.toggle("bg-red-50", overbilled)
    row.classList.toggle("bg-green-50", !overbilled && (amount !== 0 || stored !== 0))
    // Gray only when PAST applications already billed 100% (Larry 2026-07-23):
    // prev is fixed for this app, so entering 100% this period keeps white
    // fields. Still editable either way — a line may legitimately run past 100%.
    const atFull = sched > 0 ? prev >= sched - 0.02 : (sched < 0 ? prev <= sched + 0.02 : false)
    row.querySelectorAll("input[data-field]").forEach((inp) => {
      inp.classList.toggle("bg-gray-100", atFull)
      inp.classList.toggle("bg-white", !atFull)
    })

    this._dirty[row.dataset.lineId] = {
      id: row.dataset.lineId,
      this_period_qty: get("this_period_qty"),
      this_period_amount: get("this_period_amount"),
      stored_materials: stored
    }
    this._queueSave()
  }

  // Spreadsheet-style keyboard travel across the billing inputs: ↑/↓ (and
  // Enter ↓) move to the same field on the neighboring row; ←/→ hop between
  // fields only when the caret sits at the edge of the text, so the arrows
  // still edit normally. Landing selects the value for easy overwrite.
  gridKeydown(e) {
    const FIELDS = ["this_period_qty", "this_period_amount", "pct", "stored_materials"]
    const input = e.target
    const field = input.dataset.field
    let target = null

    if (e.key === "ArrowUp" || e.key === "ArrowDown" || e.key === "Enter") {
      const rows = this.rowTargets.filter((r) => r.querySelector("[data-field]"))
      const idx = rows.indexOf(input.closest("tr"))
      const next = rows[idx + (e.key === "ArrowUp" ? -1 : 1)]
      target = next?.querySelector(`[data-field="${field}"]`)
    } else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      const atStart = input.selectionStart === 0 && input.selectionEnd === 0
      const atEnd = input.selectionStart === input.value.length && input.selectionEnd === input.value.length
      if (e.key === "ArrowLeft" && atStart) {
        const i = FIELDS.indexOf(field)
        if (i > 0) target = input.closest("tr").querySelector(`[data-field="${FIELDS[i - 1]}"]`)
      } else if (e.key === "ArrowRight" && atEnd) {
        const i = FIELDS.indexOf(field)
        if (i < FIELDS.length - 1) target = input.closest("tr").querySelector(`[data-field="${FIELDS[i + 1]}"]`)
      }
    } else {
      return
    }

    if (target) {
      e.preventDefault()
      target.focus()
      target.select()
    }
  }

  _queueSave() {
    if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = "Saving…"
    clearTimeout(this._saveTimer)
    this._saveTimer = setTimeout(() => this._save(), 1000)
  }

  async _save() {
    const lines = Object.values(this._dirty)
    if (!lines.length) return
    this._dirty = {}
    try {
      const resp = await fetch(this.updateLinesUrlValue, {
        method: "PATCH", headers: this._headers(), body: JSON.stringify({ lines })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Save failed")
      this._applyTotals(data)
      if (this.hasSaveStatusTarget) {
        this.saveStatusTarget.textContent = "Saved ✓"
        setTimeout(() => { this.saveStatusTarget.textContent = "Changes save automatically" }, 2000)
      }
    } catch (err) {
      lines.forEach((l) => { this._dirty[l.id] = l })
      if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = "Save failed — retrying on next edit"
      this._showToast(err.message, "alert")
    }
  }

  _applyTotals(t) {
    const m = {
      g702Original: t.original_contract_sum, g702NetCo: t.net_change_orders,
      g702Current: t.current_contract_sum, g702Completed: t.total_completed_and_stored,
      g702Retainage: t.retainage_amount, g702Earned: t.earned_less_retainage,
      g702Previous: t.previous_certificates, g702Due: t.current_payment_due,
      g702Balance: t.balance_to_finish, g702UnitPrices: t.unit_prices_billed_to_date
    }
    Object.entries(m).forEach(([target, value]) => {
      const el = this[`has${target[0].toUpperCase()}${target.slice(1)}Target`] && this[`${target}Target`]
      if (el && value !== undefined) el.textContent = "$" + this._fmt(value)
    })
    if (this.hasFootPeriodTarget) this.footPeriodTarget.textContent = "$" + this._fmt(t.this_period_total)
    if (this.hasFootStoredTarget) this.footStoredTarget.textContent = "$" + this._fmt(t.stored_materials_total)
    if (this.hasFootCompletedTarget) this.footCompletedTarget.textContent = "$" + this._fmt(t.total_completed_and_stored)
    if (this.hasFootBalanceTarget) this.footBalanceTarget.textContent = "$" + this._fmt(t.balance_to_finish)
    if (this.hasFootTotalPctTarget) {
      const sum = Number(t.current_contract_sum) || 0
      this.footTotalPctTarget.textContent = sum !== 0 ? this._pctFmt((t.total_completed_and_stored / sum) * 100) : "—"
    }
    if (this.hasOverbilledBannerTarget) {
      this.overbilledBannerTarget.classList.toggle("hidden", !t.overbilled_count)
      if (t.overbilled_count) {
        this.overbilledTextTarget.textContent =
          `${t.overbilled_count} line${t.overbilled_count > 1 ? "s" : ""} bill past 100% of scheduled value or past a unit price limit — you can still send it, but double-check the highlighted rows.`
      }
    }
    if (this.hasFootCheckTarget) this.footCheckTarget.classList.toggle("opacity-30", !t.totals_foot)
  }

  // Billing period is set at create time but was previously stuck if skipped —
  // both dates stay editable while the app is a draft (Larry 2026-08-25).
  async periodChanged() {
    const body = {}
    if (this.hasPeriodStartInputTarget) body.period_start = this.periodStartInputTarget.value
    if (this.hasPeriodEndInputTarget) body.period_end = this.periodEndInputTarget.value
    if (body.period_end === "") { this._showToast("Period end is required", "alert"); return }
    const resp = await fetch(this.updateUrlValue, {
      method: "PATCH", headers: this._headers(), body: JSON.stringify(body)
    })
    const data = await resp.json().catch(() => ({}))
    if (resp.ok) this._showToast("Billing period updated")
    else this._showToast(data.error || "Could not update the billing period", "alert")
  }

  async retainageChanged() {
    const resp = await fetch(this.updateUrlValue, {
      method: "PATCH", headers: this._headers(),
      body: JSON.stringify({ retainage_pct: this.retainageInputTarget.value })
    })
    const data = await resp.json()
    if (resp.ok) { this._applyTotals(data); this._showToast("Retainage updated") }
    else this._showToast(data.error || "Could not update retainage", "alert")
  }

  // AI reads the project's state and fills a sensible default retainage %.
  async suggestRetainage() {
    const btn = this.hasSuggestRetainageBtnTarget ? this.suggestRetainageBtnTarget : null
    if (btn) btn.disabled = true
    try {
      const resp = await fetch(this.suggestRetainageUrlValue, { headers: { Accept: "application/json" } })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Couldn't suggest a retainage")
      this.retainageInputTarget.value = data.pct
      const save = await fetch(this.updateUrlValue, {
        method: "PATCH", headers: this._headers(),
        body: JSON.stringify({ retainage_pct: data.pct })
      })
      const saved = await save.json().catch(() => ({}))
      if (save.ok) this._applyTotals(saved)
      this._showToast(`${data.state}: ${data.pct}% retainage${data.note ? ` — ${data.note}` : ""}`)
    } catch (err) {
      this._showToast(err.message, "alert")
    } finally {
      if (btn) btn.disabled = false
    }
  }

  async refreshLines() {
    const resp = await fetch(this.refreshUrlValue, { method: "POST", headers: this._headers() })
    if (resp.ok) window.location.reload()
    else this._showToast("Could not refresh lines", "alert")
  }

  // ── Lifecycle actions ──────────────────────────────────────────────────
  openSubmitModal() {
    if (this.hasSubmitModalDueTarget && this.hasG702DueTarget) {
      this.submitModalDueTarget.textContent = `Current payment due: ${this.g702DueTarget.textContent}.`
    }
    this.submitModalTarget.classList.remove("hidden")
  }

  // Trash icon: only the most recent pay app can be deleted. If a later one
  // still exists, explain that instead of opening the confirm modal.
  attemptDelete() {
    if (this.deleteBlockedValue) {
      this._showToast(this.deleteBlockedMessageValue || "Delete the newer pay applications first.", "alert")
      return
    }
    this.openVoidModal()
  }

  openVoidModal() { this.voidModalTarget.classList.remove("hidden") }
  openReopenModal() { this.reopenModalTarget.classList.remove("hidden") }

  // "Withdraw signatures & save as draft" — wrong approvals go back to an
  // editable draft; the server enforces approver/admin + guards.
  async reopenApp() {
    const reason = this.hasReopenReasonTarget ? this.reopenReasonTarget.value.trim() : ""
    await this._postAndReload(this.reopenUrlValue, { reason },
      "Signatures withdrawn — back to draft, make your changes and resubmit")
  }

  async submit() { await this._postAndReload(this.submitUrlValue, {}, "Pay application submitted") }
  async approve() { await this._postAndReload(this.approveUrlValue, {}, "Approved — grid is locked") }
  async revise() { await this._postAndReload(this.reviseUrlValue, {}, "Back to draft — make your changes and resubmit") }
  async voidApp() { await this._postAndReload(this.voidUrlValue, {}, "Application voided") }

  // Delete (not void) — a pay request is removed for good; jump back to the
  // list since the current page no longer exists.
  async deleteApp() {
    try {
      const resp = await fetch(this.deleteUrlValue, { method: "DELETE", headers: this._headers() })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Could not delete")
      sessionStorage.setItem("builder-finance.flash", "Pay application deleted")
      window.location.href = data.redirect || this.deleteUrlValue.replace(/\/\d+$/, "")
    } catch (err) {
      this.closeModals()
      this._showToast(err.message, "alert")
    }
  }
  async internalApprove() { await this._postAndReload(this.internalApproveUrlValue, {}, "Internal sign-off recorded") }

  async reject() {
    await this._postAndReload(this.rejectUrlValue, { reason: this.rejectReasonTarget.value }, "Rejected — sent back to the GC")
  }

  async sendFollowup() {
    const resp = await fetch(this.followupUrlValue, { method: "POST", headers: this._headers(), body: JSON.stringify({ kind: "gentle" }) })
    const data = await resp.json()
    if (resp.ok) this._showToast("Payment reminder sent to the owner")
    else this._showToast(data.error || "Could not send the reminder", "alert")
  }

  async _postAndReload(url, body, message) {
    try {
      const resp = await fetch(url, { method: "POST", headers: this._headers(), body: JSON.stringify(body) })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Action failed")
      sessionStorage.setItem("builder-finance.flash", message)
      window.location.reload()
    } catch (err) {
      this.closeModals()
      this._showToast(err.message, "alert")
    }
  }

  // ── Payments ───────────────────────────────────────────────────────────
  openPaymentModal() { this.paymentModalTarget.classList.remove("hidden") }

  async recordPayment() {
    const resp = await fetch(this.paymentUrlValue, {
      method: "POST", headers: this._headers(),
      body: JSON.stringify({
        amount: this.paymentAmountTarget.value,
        paid_on: this.paymentDateTarget.value,
        method: this.paymentMethodTarget.value,
        reference: this.paymentReferenceTarget.value
      })
    })
    const data = await resp.json()
    if (!resp.ok) return this._showToast(data.error || "Could not record the payment", "alert")
    sessionStorage.setItem("builder-finance.flash", data.fully_paid ? "Payment recorded — paid in full 🎉" : "Payment recorded")
    window.location.reload()
  }

  async deletePayment(e) {
    const url = e.currentTarget.dataset.url
    const row = e.currentTarget.closest("tr")
    const resp = await fetch(url, { method: "DELETE", headers: this._headers() })
    if (resp.ok) { row.remove(); this._showToast("Payment removed") }
  }

  // ── Stored materials ───────────────────────────────────────────────────
  addStoredRow() { this.storedModalTarget.classList.remove("hidden") }

  async saveStoredRow() {
    const body = {}
    this.storedModalTarget.querySelectorAll("[data-stored-field]").forEach((el) => {
      body[el.dataset.storedField] = el.value
    })
    const resp = await fetch(this.storedUrlValue, { method: "POST", headers: this._headers(), body: JSON.stringify(body) })
    const data = await resp.json()
    if (!resp.ok) return this._showToast(data.error || "Could not add the material", "alert")
    if (this.hasStoredEmptyTarget) this.storedEmptyTarget.remove()
    this.storedBodyTarget.insertAdjacentHTML("beforeend", this._storedRowHtml(data))
    this.storedModalTarget.querySelectorAll("[data-stored-field]").forEach((el) => { el.value = "" })
    this.closeModals()
    this._showToast("Stored material added")
  }

  async deleteStoredRow(e) {
    const row = e.currentTarget.closest("tr")
    const resp = await fetch(`${this.storedUrlValue}/${row.dataset.smId}`, { method: "DELETE", headers: this._headers() })
    if (resp.ok) { row.remove(); this._showToast("Removed") }
  }

  _storedRowHtml(sm) {
    const esc = (s) => (s || "").toString().replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    return `<tr data-sm-id="${sm.id}">
      <td class="px-2 py-1.5">${esc(sm.description)}</td>
      <td class="px-2 py-1.5 text-gray-500">${esc(sm.invoice_number)}</td>
      <td class="px-2 py-1.5 text-gray-500">${esc(sm.transmittal_number)}</td>
      <td class="px-2 py-1.5 text-right tabular-nums">$${this._fmt(sm.stored_previously)}</td>
      <td class="px-2 py-1.5 text-right tabular-nums">$${this._fmt(sm.stored_this_month)}</td>
      <td class="px-2 py-1.5 text-right tabular-nums">$${this._fmt(sm.incorporated)}</td>
      <td class="px-2 py-1.5 text-right tabular-nums font-medium">$${this._fmt(sm.remaining)}</td>
      <td class="px-2 py-1.5 text-right"><button data-action="click->pay-app-detail#deleteStoredRow" class="text-gray-300 hover:text-red-500 cursor-pointer">✕</button></td>
    </tr>`
  }

  // ── Attachments (chips) ────────────────────────────────────────────────
  async _loadAttachments() {
    if (!this.hasAttachmentListTarget) return
    try {
      const resp = await fetch(this.attachmentsUrlValue, { headers: { "Accept": "application/json" } })
      const files = await resp.json()
      this.attachmentListTarget.innerHTML = ""
      if (!files.length) {
        this.attachmentListTarget.innerHTML = '<span class="text-[13px] text-gray-400">No files attached.</span>'
        return
      }
      files.forEach((f) => this.attachmentListTarget.insertAdjacentHTML("beforeend", this._chipHtml(f)))
    } catch {
      this.attachmentListTarget.innerHTML = '<span class="text-[13px] text-gray-400">Could not load files.</span>'
    }
  }

  _chipHtml(f) {
    const esc = (s) => (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    return `<span class="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 rounded-lg px-2.5 py-1 text-[13px]" data-attachment-id="${f.id}">
      <svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
      <a href="${f.url}" target="_blank" class="hover:text-brand max-w-[180px] truncate">${esc(f.filename)}</a>
      ${this.canManageValue ? `<button data-action="click->pay-app-detail#removeAttachment" data-id="${f.id}" class="text-gray-400 hover:text-red-500 cursor-pointer">✕</button>` : ""}
    </span>`
  }

  async filesChosen(e) {
    const files = Array.from(e.target.files || [])
    e.target.value = ""
    await this._uploadFiles(files)
  }

  // ── Drag-and-drop onto the attachments box (same as the PCO) ──
  dragOver(e) {
    if (!this.canManageValue) return
    e.preventDefault()
    e.currentTarget.classList.add("ring-2", "ring-brand", "bg-brand/5")
  }

  dragLeave(e) {
    e.currentTarget.classList.remove("ring-2", "ring-brand", "bg-brand/5")
  }

  async dropFiles(e) {
    e.preventDefault()
    e.currentTarget.classList.remove("ring-2", "ring-brand", "bg-brand/5")
    if (!this.canManageValue) return
    await this._uploadFiles(Array.from(e.dataTransfer?.files || []))
  }

  async _uploadFiles(files) {
    if (!files.length) return
    if (this.hasAttachmentEmptyTarget) this.attachmentEmptyTarget.remove()
    await Promise.all(files.map((file) => this._uploadFile(file)))
    this._loadAttachments()
  }

  async _uploadFile(file) {
    const chip = document.createElement("span")
    chip.className = "inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 rounded-lg px-2.5 py-1 text-[13px]"
    chip.innerHTML = `<svg class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg><span class="max-w-[180px] truncate">${file.name}</span>`
    this.attachmentListTarget.appendChild(chip)
    try {
      const presign = await fetch(this.presignUrlValue, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
      })
      const pd = await presign.json()
      if (!presign.ok) throw new Error(pd.error || "Upload refused")
      const u = pd.uploads[0]
      const put = await fetch(u.upload_url, { method: "PUT", headers: { "Content-Type": u.content_type }, body: file })
      if (!put.ok) throw new Error("S3 upload failed")
      const reg = await fetch(this.registerUrlValue, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: [{ cache_name: u.cache_name, filename: u.filename }] })
      })
      if (!reg.ok) throw new Error("Could not register the file")
    } catch (err) {
      this._showToast(`${file.name}: ${err.message}`, "alert")
    } finally {
      chip.remove()
    }
  }

  async removeAttachment(e) {
    const id = e.currentTarget.dataset.id
    const resp = await fetch(`${this.attachmentsUrlValue}/${id}`, { method: "DELETE", headers: this._headers() })
    if (resp.ok) e.currentTarget.closest("[data-attachment-id]").remove()
  }

  // ── AI Import Progress (KC-87) ─────────────────────────────────────────
  // One button, two sources: pull from ConsLog (the A3 drafter below) or
  // drop the pay estimate they were sent. Both land in the review modal.
  openAiImport() {
    this._showAiImportStep("choices")
    this.aiImportModalTarget.classList.remove("hidden")
  }

  closeAiImport() {
    this.aiImportModalTarget.classList.add("hidden")
    if (this._aiMode === "file" && this._aiPoll) {
      clearInterval(this._aiPoll)
      this._aiPoll = null
      this._aiRunId = null
      this._resetAiBtn()
    }
  }

  aiImportDragOver(e) {
    e.preventDefault()
    this.aiImportDropTarget.classList.add("border-blue-400", "bg-blue-50")
  }

  aiImportDragLeave(e) {
    if (this.aiImportDropTarget.contains(e.relatedTarget)) return
    this.aiImportDropTarget.classList.remove("border-blue-400", "bg-blue-50")
  }

  aiImportDrop(e) {
    e.preventDefault()
    e.stopPropagation()
    this.aiImportDropTarget.classList.remove("border-blue-400", "bg-blue-50")
    const file = e.dataTransfer?.files?.[0]
    if (file) this._aiImportFile(file)
  }

  aiImportChosen(e) {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (file) this._aiImportFile(file)
  }

  // presign → S3 PUT → start the reader → poll, like every other upload.
  async _aiImportFile(file) {
    this._showAiImportStep("progress", file.name, "Uploading…")
    this.aiDraftBtnTarget.disabled = true
    try {
      const presign = await fetch(this.presignUrlValue, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
      })
      const pd = await presign.json()
      if (!presign.ok) throw new Error(pd.error || "Upload refused")
      const u = pd.uploads[0]
      const put = await fetch(u.upload_url, { method: "PUT", headers: { "Content-Type": u.content_type }, body: file })
      if (!put.ok) throw new Error("The upload failed — try again")
      const resp = await fetch(this.aiImportUrlValue, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ s3_key: `uploads/tmp/${u.cache_name}`, filename: u.filename, mime_type: u.content_type })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not read the file")
      // Cancelled while uploading: don't start waiting on the reader.
      if (this.aiImportModalTarget.classList.contains("hidden")) { this._resetAiBtn(); return }
      this._showAiImportStep("progress", file.name, "The AI is reading every line — this can take a minute.")
      this._pollAiDraft(data.run_id, "file")
    } catch (err) {
      this._showToast(`${file.name}: ${err.message}`, "alert")
      this._showAiImportStep("choices")
      this._resetAiBtn()
    }
  }

  _showAiImportStep(step, filename = "", message = "") {
    const progress = step === "progress"
    this.aiImportChoicesTarget.classList.toggle("hidden", progress)
    this.aiImportProgressTarget.classList.toggle("hidden", !progress)
    this.aiImportProgressTarget.classList.toggle("flex", progress)
    if (!progress) { this.aiImportProgressTarget.innerHTML = ""; return }
    this.aiImportProgressTarget.innerHTML = `
      <span class="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 rounded-lg px-2.5 py-1 text-[13px]">
        <svg class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg>
        <span class="max-w-[260px] truncate">${this._esc(filename)}</span>
      </span>
      <p class="text-[13px] text-gray-500">${this._esc(message)}</p>`
  }

  // ── AI draft (A3) — "Pull from ConsLog" ────────────────────────────────
  async aiDraft() {
    if (this.hasAiImportModalTarget) this.aiImportModalTarget.classList.add("hidden")
    this.aiDraftBtnTarget.disabled = true
    this.aiDraftBtnTarget.innerHTML = "Reading jobsite data…"
    try {
      const resp = await fetch(this.aiDraftUrlValue, { method: "POST", headers: this._headers() })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not start the AI draft")
      this._pollAiDraft(data.run_id, "conslog")
    } catch (err) {
      this._showToast(err.message, "alert")
      this._resetAiBtn()
    }
  }

  // mode: "conslog" (jobsite drafter) or "file" (uploaded pay estimate).
  _pollAiDraft(runId, mode = "conslog") {
    clearInterval(this._aiPoll)
    let ticks = 0
    this._aiMode = mode
    this._aiRunId = runId
    const stop = () => { clearInterval(this._aiPoll); this._aiPoll = null; this._resetAiBtn() }
    this._aiPoll = setInterval(async () => {
      ticks++
      if (ticks > 72) {
        stop()
        if (mode === "file") this.closeAiImport()
        this._showToast("The AI took too long — try again in a minute.", "alert")
        return
      }
      let data
      try {
        const resp = await fetch(`${this.aiStatusUrlValue}?run_id=${runId}`, { headers: { "Accept": "application/json" } })
        data = await resp.json()
      } catch { return }
      if (this._aiRunId !== runId) return // closed while reading
      if (data.status === "done") {
        stop()
        if (mode === "file") {
          this.aiImportModalTarget.classList.add("hidden")
          this._openImportReview(data.result || {})
        } else {
          this._openAiReview(data.result || {})
        }
      } else if (data.status === "failed") {
        stop()
        if (mode === "file") this._showAiImportStep("choices")
        this._showToast(data.error || "The AI didn't finish — bill manually or try again.", "alert")
      }
    }, 2500)
  }

  // Nothing is applied yet — every suggestion is listed with a checkbox
  // (all checked); the user validates and clicks Update, or cancels.
  _openAiReview(result) {
    const esc = (t) => { const d = document.createElement("div"); d.textContent = t == null ? "" : String(t); return d.innerHTML }
    const lines = (result.lines || []).filter((l) =>
      l.pct !== null && l.pct !== undefined &&
      this.rowTargets.some((r) => r.dataset.lineId === String(l.id)))
    if (!lines.length) {
      this._showToast(result.summary ? `AI draft: ${result.summary}` : "The AI had no billing suggestions for this period.")
      return
    }

    this.aiReviewRowsTarget.innerHTML = lines.map((l) => {
      const row = this.rowTargets.find((r) => r.dataset.lineId === String(l.id))
      // The AI suggests CUMULATIVE % complete; the grid's % field is now
      // per-application, so compare cumulative-to-cumulative here and let
      // applyAiReview convert to a period % on Update.
      const sched = parseFloat(row.dataset.scheduled) || 0
      const prev = parseFloat(row.dataset.previous) || 0
      const stored = this._num(row.querySelector('[data-field="stored_materials"]')?.value)
      const amountNow = this._num(row.querySelector('[data-field="this_period_amount"]')?.value)
      const current = sched !== 0 ? this._round(((prev + amountNow + stored) / sched) * 100) : (parseFloat(l.prev_pct) || 0)
      const up = parseFloat(l.pct) >= current
      // What this suggestion would bill THIS period, in dollars (and qty when
      // the line is unit-priced) — same math applyAiReview runs on Update.
      const unitPrice = parseFloat(row.dataset.unitPrice) || 0
      const amount = this._round((sched * parseFloat(l.pct)) / 100 - prev - stored)
      const qty = unitPrice !== 0 ? this._round3(amount / unitPrice) : null
      const math = qty !== null ? `${qty} × $${this._fmt(unitPrice)} · ` : ""
      return `
        <label class="flex items-start gap-2.5 py-2 cursor-pointer hover:bg-gray-50 rounded px-1">
          <input type="checkbox" checked value="${l.id}" data-ai-review-check
                 data-action="change->pay-app-detail#syncAiReviewAll"
                 class="mt-0.5 rounded border-gray-300 text-brand focus:ring-brand">
          <span class="min-w-0 flex-1">
            <span class="block text-[13px] text-gray-800 truncate">${esc(l.desc || `Line ${l.id}`)}</span>
            ${l.why ? `<span class="block text-[13px] text-gray-400 truncate">${esc(l.why)}</span>` : ""}
          </span>
          <span class="text-right whitespace-nowrap">
            <span class="block text-[13px] tabular-nums ${up ? "text-emerald-600" : "text-amber-600"}">
              ${current}% → <span class="font-semibold">${l.pct}%</span>
            </span>
            <span class="block text-[13px] tabular-nums text-gray-500">${math}<span class="font-medium text-gray-700">$${this._fmt(amount)}</span> this period</span>
          </span>
        </label>`
    }).join("")
    this._aiReviewLines = lines
    this._setAiReviewMode("conslog")
    if (this.hasAiReviewAllTarget) this.aiReviewAllTarget.checked = true
    this._syncAiReviewApplyLabel()
    this.aiReviewModalTarget.classList.remove("hidden")
  }

  // File import (KC-87): dollars, not %, so the application can bill exactly
  // what the file bills. Lines the file doesn't bill but that already carry
  // an amount here are listed too ("→ $0"), so the totals can match.
  _openImportReview(result) {
    const rowFor = (id) => this.rowTargets.find((r) => r.dataset.lineId === String(id))
    const fileLines = (result.lines || []).filter((l) => rowFor(l.id))
    const inFile = new Set(fileLines.map((l) => String(l.id)))
    const clearLines = this.rowTargets.filter((r) => !inFile.has(r.dataset.lineId) && this._rowNow(r) !== 0)
      .map((r) => ({ id: r.dataset.lineId, clear: true, amount: 0, stored: 0, qty: 0,
                     desc: [r.cells[0]?.textContent.trim(), r.cells[1]?.textContent.trim()].filter(Boolean).join(" — ") }))
    const lines = fileLines.concat(clearLines)
    this._importResult = result
    const period = this._importPeriodChange(result.period)
    if (!lines.length && !period) {
      this._showToast(result.summary ? `Nothing to fill: ${result.summary}` : "The AI found no billing for this application in that file.", "alert")
      return
    }

    this.aiReviewRowsTarget.innerHTML = lines.map((l) => {
      const row = rowFor(l.id)
      const now = this._rowNow(row)
      const next = this._round((Number(l.amount) || 0) + (Number(l.stored) || 0))
      const sub = l.clear ? "Not in the file — cleared so the totals match"
        : (l.file_desc && !String(l.desc || "").includes(l.file_desc) ? `File: ${l.file_desc}` : "")
      const stored = Number(l.stored) ? ` · incl. $${this._fmt(l.stored)} stored` : ""
      return `
        <label class="flex items-start gap-2.5 py-2 cursor-pointer hover:bg-gray-50 rounded px-1">
          <input type="checkbox" checked value="${this._esc(l.id)}" data-ai-review-check
                 data-action="change->pay-app-detail#syncAiReviewAll"
                 class="mt-0.5 rounded border-gray-300 text-brand focus:ring-brand">
          <span class="min-w-0 flex-1">
            <span class="block text-[13px] text-gray-800 truncate">${this._esc(l.desc || `Line ${l.id}`)}</span>
            ${sub ? `<span class="block text-[13px] ${l.clear ? "text-amber-600" : "text-gray-400"} truncate">${this._esc(sub)}</span>` : ""}
          </span>
          <span class="text-right whitespace-nowrap">
            <span class="block text-[13px] tabular-nums text-gray-500">$${this._fmt(now)} → <span class="font-semibold text-gray-800">$${this._fmt(next)}</span></span>
            <span class="block text-[13px] tabular-nums text-gray-400">this period${stored}</span>
          </span>
        </label>`
    }).join("")
    // The file's billing period, when it differs from this application's —
    // its own checkbox at the top, like the lines (period_start/end boxes).
    if (period) {
      this.aiReviewRowsTarget.insertAdjacentHTML("afterbegin", `
        <label class="flex items-start gap-2.5 py-2 cursor-pointer hover:bg-gray-50 rounded px-1">
          <input type="checkbox" checked value="period" data-ai-review-check
                 data-action="change->pay-app-detail#syncAiReviewAll"
                 class="mt-0.5 rounded border-gray-300 text-brand focus:ring-brand">
          <span class="min-w-0 flex-1">
            <span class="block text-[13px] text-gray-800">Billing period</span>
            <span class="block text-[13px] text-gray-400">From the file</span>
          </span>
          <span class="text-right whitespace-nowrap text-[13px] tabular-nums">
            <span class="block text-gray-500">${this._esc(period.from)}</span>
            <span class="block">→ <span class="font-semibold text-gray-800">${this._esc(period.to)}</span></span>
          </span>
        </label>`)
    }
    this._aiReviewLines = lines
    this._setAiReviewMode("file")
    if (this.hasAiReviewAllTarget) this.aiReviewAllTarget.checked = true
    this._syncAiReviewApplyLabel()
    this.aiReviewModalTarget.classList.remove("hidden")
  }

  // The file's period vs the header's date boxes; null when it adds nothing.
  _importPeriodChange(p) {
    if (!p || (!p.start && !p.end)) return null
    const nowStart = this.hasPeriodStartInputTarget ? this.periodStartInputTarget.value : ""
    const nowEnd = this.hasPeriodEndInputTarget ? this.periodEndInputTarget.value : ""
    const start = p.start || nowStart
    const end = p.end || nowEnd
    if (start === nowStart && end === nowEnd) return null
    const show = (ymd) => {
      if (!ymd) return "…"
      const [y, m, d] = ymd.split("-")
      return document.documentElement.dataset.dateOrder === "dmy" ? `${d}/${m}/${y}` : `${m}/${d}/${y}`
    }
    return { start, end, from: `${show(nowStart)} – ${show(nowEnd)}`, to: `${show(start)} – ${show(end)}` }
  }

  // This period on a row as the grid holds it now: work + stored.
  _rowNow(row) {
    const val = (f) => this._num(row.querySelector(`[data-field="${f}"]`)?.value)
    return this._round(val("this_period_amount") + val("stored_materials"))
  }

  _setAiReviewMode(mode) {
    this._aiReviewMode = mode
    this.aiReviewModalTarget.querySelectorAll("[data-ai-review-mode]").forEach((el) => {
      el.classList.toggle("hidden", el.dataset.aiReviewMode !== mode)
    })
    if (this.hasAiReviewTotalsTarget) this.aiReviewTotalsTarget.classList.toggle("hidden", mode !== "file")
  }

  // The file's printed total next to what this application bills after
  // Update (checked lines take the file's figure, the rest stay as they are).
  _syncImportTotals() {
    if (this._aiReviewMode !== "file" || !this.hasAiReviewTotalsTarget) return
    const checked = new Set(Array.from(
      this.aiReviewRowsTarget.querySelectorAll("[data-ai-review-check]:checked")).map((c) => c.value))
    const byId = new Map((this._aiReviewLines || []).map((l) => [String(l.id), l]))
    let after = 0
    let prevSum = 0
    this.rowTargets.forEach((r) => {
      const l = byId.get(r.dataset.lineId)
      after += l && checked.has(r.dataset.lineId) ? (Number(l.amount) || 0) + (Number(l.stored) || 0) : this._rowNow(r)
      prevSum += parseFloat(r.dataset.previous) || 0
    })
    after = this._round(after)

    const t = (this._importResult || {}).file_totals || {}
    const isNum = (v) => v !== null && v !== undefined && v !== "" && !isNaN(Number(v))
    let fileTotal = null, ours = after, label = "this period"
    if (isNum(t.this_period)) {
      fileTotal = this._round(Number(t.this_period) + (isNum(t.stored) ? Number(t.stored) : 0))
    } else if (isNum(t.total_to_date)) {
      fileTotal = this._round(Number(t.total_to_date)); ours = this._round(prevSum + after); label = "to date"
    }

    const box = this.aiReviewTotalsTarget
    const unmatched = (this._importResult || {}).unmatched || []
    const missing = unmatched.length
      ? `<span class="block text-amber-700 mt-1">Not found on this application: ${unmatched.map((u) =>
          `${this._esc(u.label)} ($${this._fmt(u.amount)})`).join(", ")}</span>`
      : ""
    if (fileTotal === null) {
      box.className = "mb-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-[13px] text-gray-600"
      box.innerHTML = `This application after Update: <span class="font-semibold tabular-nums">$${this._fmt(ours)}</span> ${label}. The file shows no total to compare.${missing}`
      return
    }
    const diff = this._round(ours - fileTotal)
    const match = Math.abs(diff) < 0.01
    box.className = `mb-3 rounded-lg border px-3 py-2 text-[13px] ${match ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`
    box.innerHTML = `File: <span class="font-semibold tabular-nums">$${this._fmt(fileTotal)}</span> ${label} ·
      This application after Update: <span class="font-semibold tabular-nums">$${this._fmt(ours)}</span>
      <span class="block font-medium">${match ? "✓ Totals match" : `$${this._fmt(Math.abs(diff))} ${diff > 0 ? "more" : "less"} than the file`}</span>${missing}`
  }

  toggleAiReviewAll() {
    const on = this.aiReviewAllTarget.checked
    this.aiReviewRowsTarget.querySelectorAll("[data-ai-review-check]").forEach((c) => { c.checked = on })
    this._syncAiReviewApplyLabel()
  }

  syncAiReviewAll() {
    const boxes = Array.from(this.aiReviewRowsTarget.querySelectorAll("[data-ai-review-check]"))
    if (this.hasAiReviewAllTarget) this.aiReviewAllTarget.checked = boxes.every((c) => c.checked)
    this._syncAiReviewApplyLabel()
  }

  _syncAiReviewApplyLabel() {
    if (!this.hasAiReviewApplyTarget) return
    const ticked = Array.from(this.aiReviewRowsTarget.querySelectorAll("[data-ai-review-check]:checked"))
    const n = ticked.filter((c) => c.value !== "period").length
    this.aiReviewApplyTarget.textContent = n ? `Update ${n} line${n === 1 ? "" : "s"}` : "Update"
    this.aiReviewApplyTarget.disabled = ticked.length === 0
    this._syncImportTotals()
  }

  applyAiReview() {
    const checked = new Set(Array.from(
      this.aiReviewRowsTarget.querySelectorAll("[data-ai-review-check]:checked")
    ).map((c) => c.value))
    if (this._aiReviewMode === "file") return this._applyImportReview(checked)
    let applied = 0
    ;(this._aiReviewLines || []).forEach((l) => {
      if (!checked.has(String(l.id))) return
      const row = this.rowTargets.find((r) => r.dataset.lineId === String(l.id))
      const pctInput = row?.querySelector('[data-field="pct"]')
      if (!pctInput) return
      // AI % is cumulative; the field is per-application — convert so the
      // period amount comes out to (cumulative − previous − stored).
      const sched = parseFloat(row.dataset.scheduled) || 0
      const prev = parseFloat(row.dataset.previous) || 0
      const stored = this._num(row.querySelector('[data-field="stored_materials"]')?.value)
      const amount = this._round((sched * parseFloat(l.pct)) / 100 - prev - stored)
      pctInput.value = sched !== 0 ? this._round((amount / sched) * 100) : 0
      pctInput.dispatchEvent(new Event("input", { bubbles: true }))
      applied++
    })
    this.aiReviewModalTarget.classList.add("hidden")
    track("Pay App AI Import Applied", { source: "conslog", line_count: applied })
    this._showToast(`Applied ${applied} AI suggestion${applied === 1 ? "" : "s"} — review before submitting`)
  }

  // Writes the file's dollars into the grid through the normal input path,
  // so the qty / % / totals math and the autosave run as if typed. Unit
  // price lines bill by quantity (their $ box is read-only).
  _applyImportReview(checked) {
    let applied = 0
    const put = (row, field, value) => {
      const el = row.querySelector(`[data-field="${field}"]`)
      if (!el || el.readOnly) return false
      el.value = value
      el.dispatchEvent(new Event("input", { bubbles: true }))
      return true
    }
    ;(this._aiReviewLines || []).forEach((l) => {
      if (!checked.has(String(l.id))) return
      const row = this.rowTargets.find((r) => r.dataset.lineId === String(l.id))
      if (!row) return
      const amount = Number(l.amount) || 0
      const unitPriceLine = row.dataset.unitPriceLine === "1"
      if (unitPriceLine) {
        const unitPrice = parseFloat(row.dataset.unitPrice) || 0
        const qty = l.qty !== null && l.qty !== undefined ? Number(l.qty) : (unitPrice ? this._round3(amount / unitPrice) : 0)
        put(row, "this_period_qty", this._fmtQty(qty))
      } else {
        put(row, "this_period_amount", amount ? this._fmtMoneyEntry(amount) : "")
        put(row, "stored_materials", Number(l.stored) ? this._fmtMoneyEntry(Number(l.stored)) : "")
      }
      applied++
    })
    const period = checked.has("period") ? this._importPeriodChange((this._importResult || {}).period) : null
    if (period) this._applyImportPeriod(period)
    this.aiReviewModalTarget.classList.add("hidden")
    track("Pay App AI Import Applied", { source: "file", line_count: applied, period: !!period })
    const name = (this._importResult || {}).filename
    this._showToast(`Filled ${applied} line${applied === 1 ? "" : "s"}${period ? " and the billing period" : ""}${name ? ` from ${name}` : ""} — review before submitting`)
  }

  // Sets both date boxes quietly (no change event each), then saves once.
  _applyImportPeriod(period) {
    const put = (el, ymd) => {
      if (!ymd) return
      if (el._flatpickr) el._flatpickr.setDate(ymd, false)
      else el.value = ymd
    }
    if (this.hasPeriodStartInputTarget) put(this.periodStartInputTarget, period.start)
    if (this.hasPeriodEndInputTarget) put(this.periodEndInputTarget, period.end)
    this.periodChanged()
  }

  cancelAiReview() {
    this.aiReviewModalTarget.classList.add("hidden")
  }

  _resetAiBtn() {
    if (!this.hasAiDraftBtnTarget) return
    this.aiDraftBtnTarget.disabled = false
    this.aiDraftBtnTarget.innerHTML = this._aiBtnHtml
  }

  _esc(t) { const d = document.createElement("div"); d.textContent = t == null ? "" : String(t); return d.innerHTML }

  // ── Signature chain builder ────────────────────────────────────────────
  openChainModal() {
    if (!this.chainRowsTarget.children.length) this.addChainRow()
    // Refresh the application-summary block from the live G702 strip so the
    // contractor double-checks the exact numbers being signed.
    ;[["g702Current", "chainSumCurrent"], ["g702Completed", "chainSumCompleted"],
      ["g702Retainage", "chainSumRetainage"], ["g702Previous", "chainSumPrevious"],
      ["g702Due", "chainSumDue"]].forEach(([src, dst]) => {
      const cap = (s) => s[0].toUpperCase() + s.slice(1)
      if (this[`has${cap(src)}Target`] && this[`has${cap(dst)}Target`])
        this[`${dst}Target`].textContent = this[`${src}Target`].textContent
    })
    this.chainModalTarget.classList.remove("hidden")
  }

  addChainRow() {
    const node = this.chainRowTemplateTarget.content.cloneNode(true)
    this.chainRowsTarget.appendChild(node)
    const row = this.chainRowsTarget.lastElementChild
    row.querySelector("[data-chain-member]").addEventListener("change", (e) => {
      const opt = e.target.selectedOptions[0]
      const name = row.querySelector("[data-chain-name]")
      const email = row.querySelector("[data-chain-email]")
      if (opt.value) {
        name.value = opt.dataset.name || ""
        email.value = opt.dataset.email || ""
        name.readOnly = email.readOnly = true
      } else {
        name.readOnly = email.readOnly = false
      }
    })
    this._renumberChain()
  }

  removeChainRow(e) {
    e.currentTarget.closest("[data-chain-row]").remove()
    this._renumberChain()
  }

  _renumberChain() {
    this.chainRowsTarget.querySelectorAll("[data-chain-pos]").forEach((el, i) => { el.textContent = i + 1 })
  }

  async startChain() {
    const signers = Array.from(this.chainRowsTarget.querySelectorAll("[data-chain-row]")).map((row) => ({
      user_id: row.querySelector("[data-chain-member]").value || null,
      name: row.querySelector("[data-chain-name]").value,
      email: row.querySelector("[data-chain-email]").value,
      role_label: row.querySelector("[data-chain-role]").value
    }))
    if (!signers.length || signers.some((s) => !s.email)) {
      return this._showToast("Every signer needs an email address.", "alert")
    }
    try {
      const resp = await fetch(this.chainUrlValue, { method: "POST", headers: this._headers(), body: JSON.stringify({ signers }) })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not start the signature chain")
      sessionStorage.setItem("builder-finance.flash", "Sent — the first signer just got their signing link")
      window.location.reload()
    } catch (err) {
      this._showToast(err.message, "alert")
    }
  }

  // ── Shared ─────────────────────────────────────────────────────────────
  closeModals() {
    ;["submitModal", "rejectModal", "voidModal", "reopenModal", "paymentModal", "storedModal", "chainModal"].forEach((m) => {
      if (this[`has${m[0].toUpperCase()}${m.slice(1)}Target`]) this[`${m}Target`].classList.add("hidden")
    })
  }

  stopPropagation(e) { e.stopPropagation() }

  _num(v) {
    const n = parseFloat(String(v).replace(/[^0-9.\-]/g, ""))
    return isNaN(n) ? 0 : n
  }
  _round(n) { return Math.round(n * 100) / 100 }
  _round3(n) { return Math.round(n * 1000) / 1000 }
  _fmt(n) { return (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }
  // Quantities read like people write them (mirrors bf_qty): no decimals on
  // whole numbers, as many as entered otherwise ("0.4", never ".4"), blank
  // for zero. Larry 2026-07-21.
  _fmtQty(v) {
    const n = parseFloat(v)
    if (!isFinite(n) || n === 0) return ""
    return String(parseFloat(n.toFixed(4)))
  }
  // Mirrors bf_pct: whole percentages bare ("70%"), otherwise one decimal ("4.7%").
  _pctFmt(v) {
    const n = Math.round((Number(v) || 0) * 10) / 10
    return (Number.isInteger(n) ? n.toFixed(0) : n.toFixed(1)) + "%"
  }
  qtyBlur(e) { e.target.value = e.target.value.trim() === "" ? "" : this._fmtQty(this._num(e.target.value)) }
  // $ inputs settle into money format on blur — commas, cents only when they
  // exist: "3600" → "3,600", "3600.5" → "3,600.50" (mirrors bf_money_entry;
  // _num strips the commas back out everywhere, so the math never sees them).
  _fmtMoneyEntry(n) {
    const v = Number(n) || 0
    return Number.isInteger(v)
      ? v.toLocaleString("en-US", { maximumFractionDigits: 0 })
      : v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }
  moneyBlur(e) { e.target.value = e.target.value.trim() === "" ? "" : this._fmtMoneyEntry(this._num(e.target.value)) }

  _headers() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
    }
  }

  _showToast(message, type = "notice") {
    const div = document.createElement("div")
    div.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    div.setAttribute("data-controller", "flash")
    div.setAttribute("data-flash-dismiss-after-value", "5000")
    div.textContent = message
    document.body.prepend(div)
  }
}
